<template>
  <CompanyAppLayout
    page-title="W-2 Dashboard"
    page-subtitle="Employee roster by crew level — Green Hat, White Hat, Supervisors"
  >
    <div class="space-y-6 pt-4">
      <div class="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 class="text-2xl font-bold text-white">W-2 Employee Dashboard</h1>
          <p class="text-slate-400 text-sm mt-1">
            Manage W-2 hands and supervisors. Share your invite code so employees can sign up.
          </p>
        </div>
        <div class="flex items-center gap-3 flex-wrap">
          <router-link to="/company/payroll" class="btn-secondary">Open Payroll</router-link>
          <button class="btn-primary" @click="showAdd = true">+ Add Employee</button>
        </div>
      </div>

      <p v-if="error" class="text-red-400">{{ error }}</p>

      <!-- Invite code -->
      <div class="panel flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <p class="text-sm text-slate-400">Employee invite code</p>
          <p class="text-2xl font-mono font-bold text-emerald-400 tracking-wider mt-1">{{ inviteCode || '—' }}</p>
          <p class="text-xs text-slate-500 mt-1">
            Employees enter this when registering as W-2 Employee
          </p>
        </div>
        <button class="btn-secondary" :disabled="!inviteCode" @click="copyInvite">
          {{ copied ? 'Copied' : 'Copy code' }}
        </button>
      </div>

      <!-- Crew level stats -->
      <div class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <div class="stat-card">
          <p class="text-xs text-slate-400">Active</p>
          <p class="text-2xl font-bold text-white mt-1">{{ counts.active }}</p>
        </div>
        <div class="stat-card border-l-2 border-emerald-500/60">
          <p class="text-xs text-emerald-400">Green Hat Hand</p>
          <p class="text-2xl font-bold text-white mt-1">{{ counts.green_hat }}</p>
        </div>
        <div class="stat-card border-l-2 border-slate-300/60">
          <p class="text-xs text-slate-300">White Hat Hand</p>
          <p class="text-2xl font-bold text-white mt-1">{{ counts.white_hat }}</p>
        </div>
        <div class="stat-card border-l-2 border-amber-500/60">
          <p class="text-xs text-amber-400">Supervisor 1</p>
          <p class="text-2xl font-bold text-white mt-1">{{ counts.supervisor_1 }}</p>
        </div>
        <div class="stat-card border-l-2 border-orange-500/60">
          <p class="text-xs text-orange-400">Supervisor 2</p>
          <p class="text-2xl font-bold text-white mt-1">{{ counts.supervisor_2 }}</p>
        </div>
        <div class="stat-card border-l-2 border-rose-500/60">
          <p class="text-xs text-rose-400">Supervisor 3</p>
          <p class="text-2xl font-bold text-white mt-1">{{ counts.supervisor_3 }}</p>
        </div>
      </div>

      <div class="panel">
        <div class="flex items-center justify-between mb-4 gap-3 flex-wrap">
          <h2 class="text-lg font-semibold text-white">Employee Roster</h2>
          <div class="flex items-center gap-2 flex-wrap">
            <input v-model="search" type="text" placeholder="Search…" class="form-input w-48" />
            <select v-model="filterLevel" class="form-select">
              <option value="">All levels</option>
              <option value="green_hat">Green Hat Hand</option>
              <option value="white_hat">White Hat Hand</option>
              <option value="supervisor_1">Supervisor 1</option>
              <option value="supervisor_2">Supervisor 2</option>
              <option value="supervisor_3">Supervisor 3</option>
            </select>
          </div>
        </div>

        <div v-if="loading" class="text-slate-400 py-8 text-center">Loading…</div>
        <div v-else-if="!filtered.length" class="text-slate-400 py-10 text-center">
          No W-2 employees yet. Share your invite code or add someone manually.
        </div>
        <div v-else class="overflow-x-auto">
          <table class="w-full text-sm">
            <thead class="text-slate-400 border-b border-slate-700">
              <tr>
                <th class="py-2 pr-3 text-left font-medium">Employee</th>
                <th class="py-2 pr-3 text-left font-medium">Crew Level</th>
                <th class="py-2 pr-3 text-left font-medium">Rate</th>
                <th class="py-2 pr-3 text-left font-medium">Status</th>
                <th class="py-2 pr-3 text-left font-medium">Login</th>
                <th class="py-2 pr-3 text-left font-medium">Hire Date</th>
                <th class="py-2 pr-3 text-left font-medium"></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="e in filtered" :key="e.id" class="border-b border-slate-800/80 text-slate-200">
                <td class="py-3 pr-3">
                  <div class="font-medium text-white">{{ e.name }}</div>
                  <div class="text-xs text-slate-500">{{ e.email }}</div>
                </td>
                <td class="py-3 pr-3">
                  <span class="level-pill" :class="levelClass(e.crewLevel)">{{ levelLabel(e.crewLevel) }}</span>
                </td>
                <td class="py-3 pr-3">${{ Number(e.hourlyRate || 0).toLocaleString() }}/hr</td>
                <td class="py-3 pr-3 capitalize">{{ e.status }}</td>
                <td class="py-3 pr-3">
                  <span :class="e.hasLogin ? 'text-emerald-400' : 'text-slate-500'">
                    {{ e.hasLogin ? 'Yes' : 'Invite only' }}
                  </span>
                </td>
                <td class="py-3 pr-3">{{ e.hireDate || '—' }}</td>
                <td class="py-3 pr-3">
                  <select
                    class="form-select text-xs py-1"
                    :value="e.crewLevel || ''"
                    @change="changeLevel(e, ($event.target as HTMLSelectElement).value)"
                  >
                    <option value="" disabled>Set level</option>
                    <option value="green_hat">Green Hat Hand</option>
                    <option value="white_hat">White Hat Hand</option>
                    <option value="supervisor_1">Supervisor 1</option>
                    <option value="supervisor_2">Supervisor 2</option>
                    <option value="supervisor_3">Supervisor 3</option>
                  </select>
                  <button
                    v-if="e.status !== 'terminated'"
                    class="mt-2 text-xs text-red-400 hover:text-red-300"
                    @click="terminateEmployee(e)"
                  >Terminate</button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- Add employee modal -->
    <div v-if="showAdd" class="modal-backdrop" @click.self="showAdd = false">
      <div class="modal-panel">
        <h3 class="text-xl font-semibold text-white mb-4">Add W-2 Employee</h3>
        <form class="space-y-4" @submit.prevent="addEmployee">
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label class="form-label">First Name *</label>
              <input v-model="form.firstName" required class="form-input" />
            </div>
            <div>
              <label class="form-label">Last Name *</label>
              <input v-model="form.lastName" required class="form-input" />
            </div>
          </div>
          <div>
            <label class="form-label">Email *</label>
            <input v-model="form.email" type="email" required class="form-input" />
          </div>
          <div>
            <label class="form-label">Phone</label>
            <input v-model="form.phone" class="form-input" />
          </div>
          <div>
            <label class="form-label">Crew Level *</label>
            <select v-model="form.crewLevel" required class="form-select">
              <option disabled value="">Select…</option>
              <option value="green_hat">Green Hat Hand</option>
              <option value="white_hat">White Hat Hand</option>
              <option value="supervisor_1">Supervisor 1</option>
              <option value="supervisor_2">Supervisor 2</option>
              <option value="supervisor_3">Supervisor 3</option>
            </select>
          </div>
          <div>
            <label class="form-label">Hourly Rate</label>
            <input v-model.number="form.hourlyRate" type="number" min="0" step="0.01" class="form-input" />
          </div>
          <p v-if="formError" class="text-red-400 text-sm">{{ formError }}</p>
          <div class="flex justify-end gap-3 pt-2">
            <button type="button" class="btn-secondary" @click="showAdd = false">Cancel</button>
            <button type="submit" class="btn-primary" :disabled="saving">
              {{ saving ? 'Saving…' : 'Add Employee' }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </CompanyAppLayout>
</template>

<script setup lang="ts">
import { computed, onMounted, reactive, ref } from 'vue'
import CompanyAppLayout from '@/components/layout/CompanyAppLayout.vue'
import { buildApiUrl } from '@/config/api'

type Emp = {
  id: number
  name: string
  email: string
  phone?: string
  position?: string
  crewLevel?: string
  hourlyRate?: number
  status?: string
  hireDate?: string
  hasLogin?: number
}

const loading = ref(true)
const error = ref('')
const inviteCode = ref('')
const employees = ref<Emp[]>([])
const counts = reactive({
  active: 0,
  green_hat: 0,
  white_hat: 0,
  supervisor_1: 0,
  supervisor_2: 0,
  supervisor_3: 0,
  unassigned: 0,
  total: 0,
})
const search = ref('')
const filterLevel = ref('')
const showAdd = ref(false)
const saving = ref(false)
const formError = ref('')
const copied = ref(false)

const form = reactive({
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  crewLevel: '',
  hourlyRate: 0 as number,
})

const labels: Record<string, string> = {
  green_hat: 'Green Hat Hand',
  white_hat: 'White Hat Hand',
  supervisor_1: 'Supervisor 1',
  supervisor_2: 'Supervisor 2',
  supervisor_3: 'Supervisor 3',
}

function levelLabel(level?: string) {
  return labels[level || ''] || 'Unassigned'
}

function levelClass(level?: string) {
  return {
    green_hat: 'lvl-green',
    white_hat: 'lvl-white',
    supervisor_1: 'lvl-s1',
    supervisor_2: 'lvl-s2',
    supervisor_3: 'lvl-s3',
  }[level || ''] || 'lvl-none'
}

const filtered = computed(() => {
  const q = search.value.trim().toLowerCase()
  return employees.value.filter((e) => {
    if (filterLevel.value && e.crewLevel !== filterLevel.value) return false
    if (!q) return true
    return (
      e.name?.toLowerCase().includes(q) ||
      e.email?.toLowerCase().includes(q) ||
      levelLabel(e.crewLevel).toLowerCase().includes(q)
    )
  })
})

function authHeaders() {
  const token = localStorage.getItem('auth_token') || ''
  return {
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
  }
}

async function load() {
  loading.value = true
  error.value = ''
  try {
    const res = await fetch(buildApiUrl('company/w2-dashboard'), { headers: authHeaders() })
    const data = await res.json()
    if (!res.ok) throw new Error(data.error || 'Failed to load W-2 dashboard')
    inviteCode.value = data.inviteCode || ''
    employees.value = data.employees || []
    Object.assign(counts, data.counts || {})
  } catch (e: any) {
    error.value = e.message || 'Failed to load'
  } finally {
    loading.value = false
  }
}

async function copyInvite() {
  if (!inviteCode.value) return
  try {
    await navigator.clipboard.writeText(inviteCode.value)
    copied.value = true
    setTimeout(() => (copied.value = false), 2000)
  } catch {
    /* ignore */
  }
}

async function addEmployee() {
  formError.value = ''
  saving.value = true
  try {
    const res = await fetch(buildApiUrl('company/add-w2-employee'), {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify(form),
    })
    const data = await res.json()
    if (!res.ok) throw new Error(data.error || 'Could not add employee')
    showAdd.value = false
    form.firstName = ''
    form.lastName = ''
    form.email = ''
    form.phone = ''
    form.crewLevel = ''
    form.hourlyRate = 0
    await load()
  } catch (e: any) {
    formError.value = e.message || 'Save failed'
  } finally {
    saving.value = false
  }
}

async function changeLevel(emp: Emp, crewLevel: string) {
  if (!crewLevel || crewLevel === emp.crewLevel) return
  try {
    const res = await fetch(buildApiUrl('company/update-w2-employee'), {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify({ id: emp.id, crewLevel }),
    })
    const data = await res.json()
    if (!res.ok) throw new Error(data.error || 'Update failed')
    await load()
  } catch (e: any) {
    error.value = e.message || 'Update failed'
  }
}

async function terminateEmployee(emp: Emp) {
  if (!window.confirm(`Terminate ${emp.name}? Payroll history will be retained.`)) return
  try {
    const res = await fetch(buildApiUrl(`company/terminate-w2-employee/${emp.id}`), {
      method: 'POST',
      headers: authHeaders(),
      body: '{}',
    })
    const data = await res.json().catch(() => ({}))
    if (!res.ok) throw new Error(data.error || 'Terminate failed')
    await load()
  } catch (e: any) {
    error.value = e.message || 'Terminate failed'
  }
}

onMounted(load)
</script>

<style scoped>
.panel {
  @apply bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-6;
}
.stat-card {
  @apply bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-4;
}
.form-label {
  @apply block text-sm text-slate-400 mb-1;
}
.form-input,
.form-select {
  @apply w-full px-3 py-2 bg-slate-700/50 border border-slate-600/50 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-emerald-500/40;
}
.btn-primary {
  @apply inline-flex items-center px-4 py-2 rounded-xl bg-gradient-to-r from-emerald-500 to-cyan-500 text-white font-medium hover:from-emerald-600 hover:to-cyan-600;
}
.btn-secondary {
  @apply inline-flex items-center px-4 py-2 rounded-xl bg-slate-700/60 border border-slate-600 text-slate-200 hover:bg-slate-700;
}
.modal-backdrop {
  @apply fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4;
}
.modal-panel {
  @apply w-full max-w-lg bg-slate-900 border border-slate-700 rounded-2xl p-6 shadow-2xl;
}
.level-pill {
  @apply inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium;
}
.lvl-green { @apply bg-emerald-500/20 text-emerald-300; }
.lvl-white { @apply bg-slate-200/20 text-slate-100; }
.lvl-s1 { @apply bg-amber-500/20 text-amber-300; }
.lvl-s2 { @apply bg-orange-500/20 text-orange-300; }
.lvl-s3 { @apply bg-rose-500/20 text-rose-300; }
.lvl-none { @apply bg-slate-600/40 text-slate-400; }
</style>
